import React, { useState, useRef, useEffect } from 'react';
import type { SmartFillFields } from '@atlas/shared';
import { celebrate } from '../lib/celebrate.js';
import { smartFill } from '../lib/callables.js';

interface SmartFillProps {
  onFill: (partial: SmartFillFields) => void;
}

export function SmartFill({ onFill }: SmartFillProps) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) setTimeout(() => textareaRef.current?.focus(), 50);
  }, [open]);

  async function handleFill() {
    if (!text.trim()) return;

    setLoading(true);
    setError(null);
    try {
      // Gemini runs in the API (the smartFill callable), which keeps the key
      // server-side; it returns the fields already parsed.
      const { data } = await smartFill({ text });
      onFill(data.fields);
      celebrate();
      setOpen(false);
      setText('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse — try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="self-start flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#2383e2] border border-[#d3e8fb] rounded hover:bg-[#edf4fd] transition-colors"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2a10 10 0 1 0 10 10" />
          <path d="M12 8v4l3 3" />
          <path d="M18 2v4h4" />
          <path d="M22 2 18 6" />
        </svg>
        Smart fill
      </button>

      {open && (
        <div className="border border-[#e9e9e7] rounded-lg p-4 flex flex-col gap-3 text-left">
          <div>
            <h2 className="text-sm font-semibold text-[#37352f]">Smart fill</h2>
            <p className="text-xs text-[#73726e] mt-0.5">Paste an email, ticket, or notes — Gemini will extract the FTP details.</p>
          </div>

          <textarea
            ref={textareaRef}
            className="w-full h-40 px-3 py-2 text-sm border border-[#e9e9e7] rounded resize-none focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] placeholder:text-[#aeaaa4] font-mono"
            placeholder="Paste any text with FTP credentials here…"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />

          {error && (
            <p className="text-xs text-[#e03e3e] bg-[#fbe8e8] border border-[#f5c6c6] rounded px-3 py-2">{error}</p>
          )}

          <div className="flex justify-start gap-2">
            <button
              type="button"
              onClick={handleFill}
              disabled={loading || !text.trim()}
              className="px-4 py-1.5 text-sm font-medium text-white bg-[#2383e2] rounded hover:bg-[#1a73d1] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Filling…' : 'Fill form'}
            </button>
            <button type="button" onClick={() => setOpen(false)} className="px-3 py-1.5 text-sm text-[#73726e] hover:text-[#37352f] transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
